/*
  Sweet Logic landing page: ticker and relay signup.
  Ticker: the CSS animation is the ticker; this only eases its playback rate so hover and the
  pause button slow it to a stop instead of snapping. (Ported from v4 sections/ticker.liquid.)
  Signup: progressive enhancement for the relay provider (snippets/sl-landing-signup.liquid).
*/
if (!customElements.get('sl-landing-ticker')) {
  customElements.define(
    'sl-landing-ticker',
    class SlLandingTicker extends HTMLElement {
      constructor() {
        super();
        this.animation = null;
        this.rate = 1;
        this.frame = 0;
        this.paused = false;
        this.abort = new AbortController();
      }

      connectedCallback() {
        this.abort = new AbortController();
        const { signal } = this.abort;
        const track = this.querySelector('[data-track]');
        const toggle = this.querySelector('[data-toggle]');
        if (!track || !window.matchMedia) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (reduced.matches) return;

        this.animation = track.getAnimations()[0] || null;
        if (!this.animation) return;
        this.setAttribute('data-eased', '');

        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.ease(0);
        }, { signal });
        this.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse' && !this.paused) this.ease(1);
        }, { signal });

        if (toggle) {
          toggle.addEventListener('click', () => {
            this.paused = !this.paused;
            toggle.setAttribute('aria-pressed', String(this.paused));
            toggle.textContent = (this.paused ? this.dataset.playLabel : this.dataset.pauseLabel) || '';
            this.ease(this.paused ? 0 : 1);
          }, { signal });
        }

        reduced.addEventListener('change', () => {
          if (!this.animation) return;
          if (reduced.matches) this.animation.pause();
          else this.animation.play();
        }, { signal });
      }

      disconnectedCallback() {
        this.abort.abort();
        window.cancelAnimationFrame(this.frame);
      }

      ease(target) {
        window.cancelAnimationFrame(this.frame);
        const from = this.rate;
        const start = performance.now();
        const duration = 400;

        const step = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          const eased = 1 - (1 - progress) * (1 - progress);
          this.rate = from + (target - from) * eased;
          if (this.animation) this.animation.updatePlaybackRate(this.rate);
          if (progress < 1) this.frame = window.requestAnimationFrame(step);
        };
        this.frame = window.requestAnimationFrame(step);
      }
    }
  );
}

/*
  Relay signup. Validates the email, gets a Cloudflare Turnstile token (explicit render,
  appearance interaction-only) and POSTs JSON to the relay. Tokens are single use, so the
  widget is reset after every failed attempt.
*/
(() => {
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const TOKEN_WAIT_MS = 10000;

  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  class RelaySignup {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-relay-form]');
      this.input = this.form && this.form.querySelector('input[name="email"]');
      this.honeypot = this.form && this.form.querySelector('input[name="company"]');
      this.button = root.querySelector('[data-relay-button]');
      this.errorEl = root.querySelector('[data-relay-error]');
      this.container = root.querySelector('[data-relay-turnstile]');
      this.template = root.querySelector('template[data-relay-success]');
      this.endpoint = root.dataset.endpoint;
      this.siteKey = root.dataset.siteKey;
      this.token = '';
      this.widgetId = null;
      this.busy = false;
      if (!this.form || !this.input || !this.button || !this.endpoint) return;

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.button.disabled = false;
      this.mountTurnstile();
    }

    async mountTurnstile() {
      if (!this.container || !this.siteKey) return;
      if (!document.getElementById('sl-landing-turnstile-api')) {
        const script = document.createElement('script');
        script.id = 'sl-landing-turnstile-api';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      for (let i = 0; i < 150 && !window.turnstile; i += 1) await wait(100);
      if (!window.turnstile || !this.container.isConnected) return;
      this.widgetId = window.turnstile.render(this.container, {
        sitekey: this.siteKey,
        appearance: 'interaction-only',
        callback: (token) => { this.token = token; },
        'expired-callback': () => { this.token = ''; },
        'error-callback': () => { this.token = ''; },
      });
    }

    async getToken() {
      const deadline = Date.now() + TOKEN_WAIT_MS;
      while (!this.token && Date.now() < deadline) await wait(150);
      return this.token;
    }

    resetTurnstile() {
      this.token = '';
      if (window.turnstile && this.widgetId !== null) {
        try { window.turnstile.reset(this.widgetId); } catch (error) { /* widget already gone */ }
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.button.disabled = busy;
      this.button.toggleAttribute('aria-busy', busy);
      if (busy) this.button.setAttribute('aria-busy', 'true');
      this.form.setAttribute('aria-busy', String(busy));
    }

    showError(message, field) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      if (field) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setAttribute('aria-describedby', this.errorEl.id);
      } else {
        this.input.removeAttribute('aria-invalid');
        this.input.removeAttribute('aria-describedby');
      }
    }

    clearError() {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.input.removeAttribute('aria-invalid');
      this.input.removeAttribute('aria-describedby');
    }

    showSuccess() {
      if (!this.template) return;
      if (window.turnstile && this.widgetId !== null) {
        try { window.turnstile.remove(this.widgetId); } catch (error) { /* ignore */ }
      }
      const success = this.template.content.firstElementChild.cloneNode(true);
      // Same end state as the native form: only the success card remains.
      this.root.replaceChildren(success);
      success.focus();
    }

    async submit() {
      if (this.busy) return;
      const email = this.input.value.trim();
      this.clearError();

      if (email.length > 254 || !EMAIL_PATTERN.test(email)) {
        this.showError(this.root.dataset.errorInvalid, true);
        this.input.focus();
        return;
      }

      this.setBusy(true);
      let outcome = 'generic';
      try {
        const token = await this.getToken();
        if (token) {
          const response = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              turnstileToken: token,
              company: this.honeypot ? this.honeypot.value : '',
            }),
          });
          let body = null;
          try { body = await response.json(); } catch (error) { /* non-JSON body */ }
          if (response.ok && body && body.ok === true) outcome = 'ok';
          else if (response.status === 400) outcome = 'invalid';
        }
      } catch (error) {
        outcome = 'generic';
      }

      if (outcome === 'ok') {
        this.showSuccess();
        return;
      }

      this.resetTurnstile();
      this.setBusy(false);
      if (outcome === 'invalid') {
        this.showError(this.root.dataset.errorInvalid, true);
        this.input.focus();
      } else {
        this.showError(this.root.dataset.errorGeneric, false);
      }
    }
  }

  const init = (scope) => {
    scope.querySelectorAll('[data-sl-landing-relay]').forEach((root) => {
      if (root.slLandingRelay) return;
      root.slLandingRelay = new RelaySignup(root);
    });
  };

  const start = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
